// Notifications PUSH NATIVES (app Android Capacitor + FCM).
// ----------------------------------------------------------------------------
// Remplace, SUR L'APP NATIVE uniquement, le flux Web Push (qui ne marche pas en
// app native : pas de canaux Android, permission Chrome désynchronisée). Le
// navigateur / PWA desktop continue d'utiliser le Web Push (NotificationBell).
//
// Ce module : crée les vrais canaux Android, demande la permission NATIVE
// (POST_NOTIFICATIONS, fiable), récupère le token FCM, l'envoie au backend,
// gère le renouvellement et les deep links au clic.
//
// Tous les imports Capacitor sont DYNAMIQUES → le bundle web ne les charge pas.

let _cap = null;
async function cap() {
  if (!_cap) _cap = await import("@capacitor/core");
  return _cap;
}

export async function isNative() {
  try {
    const { Capacitor } = await cap();
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
}

// Version synchrone best-effort (pour un garde rapide côté composants web).
// Sur le web, window.Capacitor est absent → false.
export function isNativeSync() {
  try {
    return !!(typeof window !== "undefined" && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch (e) {
    return false;
  }
}

// Navigation : pushNative est hors React → on stocke la fonction navigate.
let _navigate = null;
export function setPushNavigator(fn) { _navigate = fn; }

// Canaux Android (alignés avec NOTIFICATION_CHANNEL_MAP côté backend helpers.py).
// importance : 5=MAX 4=HIGH(heads-up) 3=DEFAULT(son) 2=LOW(silencieux) 1=MIN.
const CHANNELS = [
  { id: "messages",     name: "Messages",                     importance: 4, description: "Nouveaux messages reçus" },
  { id: "transactions", name: "Ventes, achats & expéditions", importance: 4, description: "Objet vendu, achat confirmé, suivi de livraison" },
  { id: "paiements",    name: "Paiements & versements",       importance: 4, description: "Paiement reçu, versement, remboursement" },
  { id: "offres",       name: "Offres & négociations",        importance: 3, description: "Offres reçues, acceptées ou refusées" },
  { id: "dons",         name: "Dons & reçus",                 importance: 3, description: "Dons générés pour les associations" },
  { id: "associations", name: "Associations",                 importance: 2, description: "Validation, mises à jour d'association" },
  { id: "compte",       name: "Compte & sécurité",            importance: 3, description: "Sécurité, litiges, avis, abonnés" },
  { id: "alertes",      name: "Alertes de mes recherches",    importance: 3, description: "Une annonce correspond à une recherche enregistrée" },
  { id: "marketing",    name: "Bons plans",                   importance: 2, description: "Nouveautés et bons plans (désactivé par défaut)" },
];

let _initStarted = false;

// Initialise le push natif. `api` = client axios (pour POST /push/fcm/register).
// Idempotent (ne s'exécute qu'une fois). À appeler quand un utilisateur est
// connecté dans l'app native.
export async function initNativePush(api) {
  if (_initStarted) return;
  if (!(await isNative())) return;
  _initStarted = true;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import("@capacitor/push-notifications"));
  } catch (e) {
    _initStarted = false;
    return;
  }

  // 1) Créer les canaux Android (idempotent côté OS).
  for (const c of CHANNELS) {
    try {
      await PushNotifications.createChannel({
        id: c.id,
        name: c.name,
        description: c.description,
        importance: c.importance,
        visibility: 1,           // PUBLIC sur l'écran de verrouillage
        lights: true,
        vibration: true,
      });
    } catch (e) { /* canal déjà présent / non-Android */ }
  }

  // 2) Permission native (POST_NOTIFICATIONS) — un seul système, fiable.
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return; // l'utilisateur a refusé : on s'arrête proprement
  } catch (e) {
    return;
  }

  // 3) Listeners AVANT register (sinon on rate le 1er token).
  try {
    await PushNotifications.addListener("registration", async (token) => {
      lastFcmToken = token.value; // mémorisé pour la purge au logout (release v21)
      try { await api.post("/push/fcm/register", { token: token.value, platform: "android" }); } catch (e) { /* resync au prochain boot */ }
    });
    await PushNotifications.addListener("registrationError", () => { /* silencieux, retry au boot */ });
    // Clic sur une notification (app en arrière-plan / fermée) → deep link.
    // Même patron que NativeBootstrap.routeGivelyUrl : on passe TOUJOURS par
    // new URL() + contrôle d'hôte, jamais par un strip regex. new URL normalise
    // les antislashs (\ → /) AVANT nos gardes : un payload « /\evil.com » qui
    // traversait l'ancien replace() intact devient //evil.com → hôte ≠ gively
    // → rejeté. Ferme la classe open-redirect côté navigation native.
    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const url = action && action.notification && action.notification.data && action.notification.data.url;
      if (url && _navigate) {
        try {
          const raw = String(url);
          const u = /^https?:\/\//i.test(raw)
            ? new URL(raw)
            : new URL(raw, "https://www.gively.fr");
          if (u.hostname !== "www.gively.fr" && u.hostname !== "gively.fr") return;
          // Un pathname en « //x » serait relu comme protocol-relative par le
          // routeur : on écrase les slashs de tête pour rester chez nous.
          const path = (u.pathname + u.search + u.hash).replace(/^\/{2,}/, "/");
          _navigate(path || "/");
        } catch (e) { /* URL invalide → ignorer */ }
      }
    });
    // Réception au premier plan : l'OS n'affiche pas de bannière → à nous de
    // décider. On laisse silencieux ici (la cloche in-app + le badge suffisent),
    // pour ne pas doubler avec une notif système quand l'app est ouverte.
    await PushNotifications.addListener("pushNotificationReceived", () => { /* in-app handled by bell */ });

    // 4) S'abonner (déclenche l'event registration → token FCM).
    await PushNotifications.register();
  } catch (e) {
    /* best-effort */
  }
}

// Dernier token FCM reçu (mémoire module) — permet la purge au logout sans
// re-demander le token au plugin. Release v21 (audit parité) : sans cette purge,
// un appareil PARTAGÉ continuait de recevoir les notifications de l'ANCIEN
// compte après déconnexion.
let lastFcmToken = null;

// Désenregistre le token de cet appareil (déconnexion / changement de compte).
// `token` optionnel : par défaut on purge le dernier token enregistré.
export async function unregisterNativePush(api, token) {
  try {
    const t = token || lastFcmToken;
    if (t) await api.delete("/push/fcm/unregister", { data: { token: t } });
    lastFcmToken = null;
  } catch (e) { /* noop */ }
}
